import React, { useEffect, useRef, useState } from "react";
import { IonIcon, IonPage } from "@ionic/react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Card, Tabs, Typography, Row, Col, Result, Spin, Select, notification, Form, Divider } from "antd";
import { AreaChartOutlined, AuditOutlined, Loading3QuartersOutlined, LockOutlined } from "@ant-design/icons";
import { giftOutline } from "ionicons/icons";

//
import log from "../../utils/log";
import c from "../../utils/common";
import moment from "moment";
import metaData from "../../data/metaData";
import _ from "lodash";
import { showErrMsg } from "../../utils/error";

//
import UserPermissionSelector from "../../components/MerchantUser/UserPermissionSelector";
import Name from "../../components/General/Name";
import SectionHeader from "../../components/GetStarted/SectionHeader";
import Avatar from "boring-avatars";

//
import useAuthStore from "../../states/useAuth";
import useAuth from "../../hooks/useAuth";
import useCompany from "../../hooks/useCompany";
import useUser from "../../hooks/useUser";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const UserProfile = ({ state = {}, isLoadingSubmit, onSave }) => {
  const { data, form, onChange } = state;
  log.debug(data);

  return (
    <>
      {/* <UpgradeBanner /> */}
      <SectionHeader title={"Basic Info"} />
      <Form form={form} style={{ width: "100%" }}>
        {/* Basic Info */}
        <Avatar size={"7.5rem"} name={data?.name} />
        <Row align="middle" style={{ margin: "2rem 0" }}>
          <Col span={3} style={{ marginRight: "1rem", fontSize: "1rem" }}>
            Email
          </Col>
          <Col>
            <>{data?.email}</>
          </Col>
        </Row>
        <Row align="middle" style={{ marginBottom: "2rem" }}>
          <Col span={3} style={{ marginRight: "1rem", fontSize: "1rem" }}>
            Username
          </Col>
          <Col>
            <>{data?.name}</>
          </Col>
        </Row>
        <Row align="middle" style={{ marginBottom: "1rem" }}>
          <Col span={3} style={{ marginRight: "1rem", fontSize: "1rem" }}>
            Old Password
          </Col>
          <Col>
            <Name
              state={{
                data: data?.oldPassword,
                form: form,
                keyName: "oldPassword",
                rules: [{ required: true, message: "required" }],
                onChange: (value) => {
                  onChange({
                    oldPassword: value.name,
                  });
                },
              }}
              readOnly={false}
              placeholder={"Old Password"}
              style={{ width: "20rem" }}
            />
          </Col>
        </Row>
        <Row align="middle" style={{ marginBottom: "2rem" }}>
          <Col span={3} style={{ marginRight: "1rem", fontSize: "1rem" }}>
            New Password
          </Col>
          <Col>
            <Name
              state={{
                data: data?.newPassword,
                form: form,
                keyName: "newPassword",
                rules: [{ required: true, message: "required" }],
                onChange: (value) => {
                  onChange({
                    newPassword: value.name,
                  });
                },
              }}
              readOnly={false}
              placeholder={"New Password"}
              style={{ width: "20rem" }}
            />
          </Col>
        </Row>
      </Form>

      <Button
        size="large"
        type="primary"
        disabled={
          !data?.oldPassword ||
          !data?.newPassword ||
          data?.oldPassword?.length < 9 ||
          data?.oldPassword?.length > 16 ||
          data?.newPassword?.length < 9 ||
          data?.newPassword?.length > 16
        }
        loading={isLoadingSubmit}
        onClick={onSave}
      >
        Save
      </Button>

      <Divider />

      {/* Permission */}
      <SectionHeader
        title={"Permission"}
        description={"Manage permissions easily using our role-based permission system!"}
      />

      <UserPermissionSelector permission={data?.permission} readOnly={true} />
    </>
  );
};

const Setting = () => {
  const { auth, user: userFromState } = useAuthStore();
  const { updateUser, resetPassword } = useUser();
  const { getHomePageData } = useCompany();

  //Sider
  const [collapsed, setCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState("userProfile");

  //
  const [user, setUser] = useState(userFromState);
  const [userForm] = Form.useForm();

  //
  const [isFetchingUser, setIsFetchingUser] = useState(false);
  const [isShowErrorPage, setIsShowErrorPage] = useState(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);

  const updateUserData = (newData) => {
    // log.debug(newData);

    const update = (prev) => {
      // Create a copy of the previous shop data
      let updated = _.merge({}, prev, newData);

      // log.debug(updated);
      return updated;
    };
    setUser(update);
  };

  const submitUserForm = async () => {
    // axios
    try {
      setIsLoadingSubmit(true);
      let { oldPassword, newPassword } = user || {};
      let response = await resetPassword(user.id, { oldPassword, newPassword });

      // Update data
      setUser(response);

      // Show success notification
      notification.success({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Success
          </Title>
        ),
      });
    } catch (error) {
      showErrMsg(error);
    } finally {
      setIsLoadingSubmit(false); // Ensure loading state is cleared
    }
  };

  const onMount = async () => {
    try {
      setIsFetchingUser(true);
      if (!user || _.isEmpty(user)) {
        // fetch current user if cleared
        await getHomePageData();
      }
    } catch (error) {
      setIsShowErrorPage(true);
      notification.error({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Error
          </Title>
        ),
        description: <Text strong>Please refresh this page</Text>,
      });
    } finally {
      setIsFetchingUser(false);
    }
  };

  const registerOnRefreshEvent = () => {
    const handleBeforeUnload = (event) => {
      updateUser("whole", {});
      // const message = "Are you sure you want to leave? Changes may not be saved.";
      // event.preventDefault();
      // event.returnValue = message; // This will trigger the prompt with the message
      // return message;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload); // Properly remove the listener
    };
  };

  useEffect(() => {
    setUser(userFromState);
  }, [userFromState]);

  useEffect(() => {
    return registerOnRefreshEvent(); // Cleanup when component unmounts
  }, []);

  useEffect(() => {
    onMount();
  }, []);

  return (
    <div style={{ margin: "0 auto" }}>
      <Spin
        spinning={isFetchingUser}
        // percent="auto"
        indicator={<Loading3QuartersOutlined spin style={{ color: "white", fontSize: "2rem" }} />}
        fullscreen
      />

      {!user || _.isEmpty(user) ? null : isShowErrorPage ? (
        <Result
          status="404"
          title="404"
          subTitle="Sorry, the page you visited does not exist."
          extra={
            <Button
              type="primary"
              onClick={() => {
                window.location.reload();
              }}
            >
              Refresh
            </Button>
          }
        />
      ) : isFetchingUser ? null : (
        <>
          <Title level={2}>Setting</Title>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Tabs
                activeKey={activeTab}
                items={[
                  {
                    key: "userProfile",
                    label: "User Profile",
                    children: (
                      <UserProfile
                        state={{
                          data: user,
                          form: userForm,
                          onChange: updateUserData,
                        }}
                        isLoadingSubmit={isLoadingSubmit}
                        onSave={() => {
                          submitUserForm();
                        }}
                      />
                    ),
                  },
                  // {
                  //   key: "2",
                  //   label: "Tab 2",
                  //   children: "Content of Tab Pane 2",
                  // },
                  // {
                  //   key: "3",
                  //   label: "Tab 3",
                  //   children: "Content of Tab Pane 3",
                  // },
                ]}
                onChange={setActiveTab}
              />
            </Col>
          </Row>

          {/* <Card style={{ marginTop: "24px" }}>
                <Title level={4}>Get more customers!</Title>
                <Text>
                  50% of new customers explore products on the social media network. Gain your network right now! 🔥
                </Text>
                <div style={{ marginTop: "16px" }}>
                  <Button icon={<FacebookOutlined />} style={{ marginRight: "8px" }}>
                    Facebook
                  </Button>
                  <Button icon={<femaleOutlined />} style={{ marginRight: "8px" }}>
                    female
                  </Button>
                  <Button icon={<InstagramOutlined />}>Instagram</Button>
                </div>
              </Card> */}
        </>
      )}
    </div>
  );
};

export default Setting;
